import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useApp, formatMoney } from "@/lib/app-context";
import { monthLabel, fmtDate } from "@/lib/sheet-utils";
import { SearchBox, matches } from "@/components/SearchBox";

const Stat = ({ label, value, tone = "", testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-xs font-semibold text-slate-500">{label}</div>
    <div className={`font-mono text-xl sm:text-2xl font-extrabold mt-1 ${tone}`} data-testid={testId}>{value}</div>
  </div>
);

function Item({ s }) {
  const m = (n) => formatMoney(n, s.currency || "INR");
  const paidOut = s.settled_amount > 0;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 p-4" data-testid={`settlement-row-${s.id}`}>
      <div className="flex-1 min-w-[160px]">
        <Link to={`/staff/${s.staff_id}`} className="font-semibold hover:text-teal-700" data-testid={`settlement-staff-${s.id}`}>{s.staff_name}</Link>
        <div className="text-xs text-slate-500">Agent: {s.agent_name || "—"} · For {monthLabel(s.month)}</div>
      </div>
      <div className="text-sm text-slate-600 font-mono" data-testid={`settlement-date-${s.id}`}>{fmtDate(s.settled_at.slice(0, 10))}</div>
      <div className="text-right min-w-[140px] ml-auto">
        <div className={`font-mono font-extrabold ${paidOut ? "text-teal-700" : "text-red-600"}`} data-testid={`settlement-amount-${s.id}`}>{m(Math.abs(s.settled_amount))}</div>
        <div className="text-[11px] font-semibold text-slate-500">{paidOut ? "Paid to staff" : "Received from staff"}</div>
      </div>
    </div>
  );
}

export default function SettlementsPage() {
  const { api } = useApp();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      setItems(await api("get", "/settlements"));
    } catch {
      setItems([]);
      toast.error("Could not load settlements");
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (!items) return <div className="text-slate-500 text-sm" data-testid="settlements-loading">Loading…</div>;
  const codes = [...new Set(items.map((s) => s.currency || "INR"))];
  const sum = (pos) => (codes.length ? codes.map((c) => (
    <div key={c}>{formatMoney(items.filter((s) => (s.currency || "INR") === c && (pos ? s.settled_amount > 0 : s.settled_amount < 0)).reduce((t, s) => t + Math.abs(s.settled_amount), 0), c)}</div>
  )) : formatMoney(0, "INR"));
  return (
    <div className="space-y-6 fade-up" data-testid="settlements-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Settlement history</h1>
        <p className="text-sm text-slate-600 mt-1">Every final settlement made, newest first.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat label="Settlements" value={items.length} testId="settlements-count" />
        <Stat label="Paid to staff" value={sum(true)} tone="text-teal-700" testId="settlements-paid-total" />
        <Stat label="Received from staff" value={sum(false)} tone="text-red-600" testId="settlements-received-total" />
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500" data-testid="settlements-empty">
          No settlements yet. Use “Settle balance” on a staff member's yearly summary.
        </div>
      ) : (
        <>
        <SearchBox value={q} onChange={setQ} placeholder="Search staff, agent or month…" testId="settlements-search" />
        <div className="bg-white border border-slate-200 rounded-xl divide-y" data-testid="settlements-list">
          {items.filter((s) => matches(q, s.staff_name, s.agent_name, monthLabel(s.month))).map((s) => <Item key={s.id} s={s} />)}
          {!items.some((s) => matches(q, s.staff_name, s.agent_name, monthLabel(s.month))) && <div className="p-6 text-center text-slate-500" data-testid="settlements-no-match">No settlements match “{q}”.</div>}
        </div>
        </>
      )}
    </div>
  );
}
